import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, OnInit, output, QueryList, resource, signal, ViewChildren } from '@angular/core';
import { Wk8BookService } from '@core/wk8/wk8-book.service';
import { Wk8TutorService } from '@core/wk8/wk8-tutor.service';
import { LpDraggableDirective } from '@features/lp/lp-drag-and-drop/lp-draggable.directive';
import { LpParticlesService } from '@features/lp/lp-particles.service';
import { SLIDE_ANIMATION } from '@features/styles/animations';
import { Wk8AudioService } from '@features/wk8/core/wk8-audio/wk8-audio.service';
import { Wk8GlobalsService } from '@features/wk8/core/wk8-globals.service';
import { Wk8PaperPieceComponent } from '@features/wk8/core/wk8-paper-piece/wk8-paper-piece.component';
import { Wk8BookContentsComponent } from '@features/wk8/wk8-book/wk8-book-contents/wk8-book-contents.component';
import { LpWk8Book, LpWk8BookSentence, LpWk8BookSentencePart, LpWk8BookSentencePartFilter, LpWoordData } from '@typings/lp/generated';

@Component({
  selector: 'wk8-book-sentence-builder',
  imports: [LpDraggableDirective, Wk8PaperPieceComponent, NgTemplateOutlet, Wk8BookContentsComponent],
  templateUrl: './wk8-book-sentence-builder.component.html',
  styleUrl: './wk8-book-sentence-builder.component.css',
  animations: [SLIDE_ANIMATION('down')],
})
export class Wk8BookSentenceBuilderComponent implements OnInit {
  readonly #lpParticlesService = inject(LpParticlesService);
  readonly #wk8AudioService = inject(Wk8AudioService);
  readonly #wk8TutorService = inject(Wk8TutorService);
  readonly #wk8BookService = inject(Wk8BookService);
  readonly _wk8GlobalsService = inject(Wk8GlobalsService);

  sentence = input.required<LpWk8BookSentence>();
  target = input.required<LpWk8BookSentencePartFilter>();
  book = signal<LpWk8Book | undefined>(undefined);
  optionsResource = resource({
    params: () => this.target(),
    loader: ({ params }) => this.#wk8BookService.getOpenSentencePartOptions(params),
  });
  options = computed(() => this.optionsResource.value());
  finished = output();

  @ViewChildren(LpDraggableDirective) draggableDivs!: QueryList<LpDraggableDirective<LpWoordData>>;

  chosenWord = signal<LpWoordData | undefined>(undefined);
  isWordFilled = computed(() => !!this.chosenWord());

  async ngOnInit(): Promise<void> {
    this.book.set(await this.#wk8BookService.getBookForSentence(this.sentence()));

    await this.#wk8TutorService.showMechanic('madlibs');
  }

  getPartText(part: LpWk8BookSentencePart) {
    return this.#wk8BookService.getSentencePartValue(part);
  }

  async setWord(part: LpWk8BookSentencePartFilter, word: LpWoordData | undefined) {
    await this.#wk8BookService.setOpenSentencePartValue(part, word?.word);
    if (word) {
      const draggable = this.draggableDivs.find((d) => d.target() !== undefined)!;
      const el = draggable.el.nativeElement;
      this.#lpParticlesService.playPaperParticles(el);
      this.#wk8AudioService.playDraggableDrop();
    }
    this.chosenWord.set(word);
  }

  public finish() {
    this.finished.emit();
  }
}
